import React, { useRef, useState } from 'react';
import SignaturePad from 'react-signature-canvas';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { addDoc, Timestamp, doc, updateDoc, collection } from 'firebase/firestore';
import { sendAgreementEmail } from '../services/emailService';
import { PDFDocument, StandardFonts } from 'pdf-lib';

export default function AgreementForm({ rentalData, onComplete }) {
  const customerSigRef = useRef();
  const ownerSigRef = useRef();
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  const generatePDF = async () => {
    const {
      customerName,
      customerAddress = '',
      customerPhone = '',
      carMake,
      carModel,
      carRego,
      startDate,
      endDate = '', // new
      weeklyRate = '', // new
      securityDeposit = '', // new
      carColor = '',
      carYear = '',
      licenseNumber = '',
      licenseState = '',
    } = rentalData;

    const existingPdfBytes = await fetch('/Aus_Car_Rent_Application.pdf').then(res => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const form = pdfDoc.getForm();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const smallFontSize = 9;

    // Fill form fields
    form.getTextField('Name').setText(customerName);
    form.getTextField('Address').setText(customerAddress);
    form.getTextField('Phone').setText(customerPhone);
    form.getTextField('Make  Model').setText(`${carMake} ${carModel}`);
    form.getTextField('Colour').setText(carColor);
    form.getTextField('Year').setText(carYear);
    form.getTextField('Registration Plate').setText(carRego);
    form.getTextField('Rental Start Date').setText(new Date(startDate).toLocaleDateString());
    form.getTextField('Rental End Date')?.setText(endDate ? new Date(endDate).toLocaleDateString() : '');
    form.getTextField('Weekly Rate AUD')?.setText(weeklyRate ? `$${weeklyRate}` : '');
    form.getTextField('Security Deposit AUD')?.setText(securityDeposit ? `$${securityDeposit}` : '');

    // Driver license fields
    form.getTextField('Drivers Licence No').setText(licenseNumber);
    form.getTextField('State of Issue').setText(licenseState);

    // Smaller font for names and dates
    form.getTextField('Name_2').setText(customerName);
    form.getTextField('Name_2').updateAppearances(font);
    form.getTextField('Name_2').setFontSize?.(smallFontSize);

    form.getTextField('Date').setText(new Date().toLocaleDateString());
    form.getTextField('Date').updateAppearances(font);
    form.getTextField('Date').setFontSize?.(smallFontSize);

    form.getTextField('Date_2').setText(new Date().toLocaleDateString());
    form.getTextField('Date_2').updateAppearances(font);
    form.getTextField('Date_2').setFontSize?.(smallFontSize);

    form.getTextField('Name_3').setText('GoGo Rent A Car');
    form.getTextField('Name_3').updateAppearances(font);
    form.getTextField('Name_3').setFontSize?.(smallFontSize);

    // Signatures
    const customerSigDataUrl = customerSigRef.current.getCanvas().toDataURL('image/png');
    const ownerSigDataUrl = ownerSigRef.current.getCanvas().toDataURL('image/png');
    const customerSigImg = await pdfDoc.embedPng(customerSigDataUrl);
    const ownerSigImg = await pdfDoc.embedPng(ownerSigDataUrl);

    const page = pdfDoc.getPages()[pdfDoc.getPageCount() - 1];

    // Coordinates based on screenshots
    page.drawImage(customerSigImg, {
      x: 121.76,
      y: 352.25,
      width: 102,
      height: 26.4,
    });

    page.drawImage(ownerSigImg, {
      x: 121.76,
      y: 239.72,
      width: 102,
      height: 26.4,
    });

    form.flatten();

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      console.log('🔄 Starting agreement submission...');
      console.log('📄 Generating PDF...');
      const pdfBlob = await generatePDF();
      console.log('✅ PDF generated, size:', pdfBlob.size, 'bytes');
      
      console.log('📤 Uploading to Firebase Storage...');
      const fileRef = ref(storage, `agreements/${Date.now()}-${rentalData.customerName}.pdf`);
      console.log('📁 Storage path:', fileRef.fullPath);
      
      await uploadBytes(fileRef, pdfBlob);
      console.log('✅ PDF uploaded to Firebase Storage');
      
      const downloadURL = await getDownloadURL(fileRef);
      console.log('🔗 Download URL:', downloadURL);
      setPdfUrl(downloadURL);

      console.log('📨 Sending email to:', rentalData.customerEmail);

      await sendAgreementEmail({
        to_email: rentalData.customerEmail,
        cc_email: rentalData.ownerEmail,
        customer_name: rentalData.customerName,
        pdf_url: downloadURL,
      });
      console.log('✅ Email sent successfully');

      console.log('💾 Saving rental data to Firestore...');
      const rentalRef = await addDoc(collection(db, 'rentals'), {
        ...rentalData,
        startDate: Timestamp.fromDate(new Date(rentalData.startDate)),
        endDate: rentalData.endDate ? Timestamp.fromDate(new Date(rentalData.endDate)) : '',
        agreementURL: downloadURL,
        createdAt: Timestamp.now(),
        returned: false,
        status: 'active',
      });
      console.log('✅ Rental saved to Firestore, ID:', rentalRef.id);

      console.log('🚗 Updating car status...');
      await updateDoc(doc(db, 'cars', rentalData.carId), {
        status: 'rented',
        currentRentalId: rentalRef.id,
      });
      console.log('✅ Car status updated');

      alert('Agreement completed and saved!');
      if (onComplete) onComplete();
    } catch (err) {
      console.error('❌ Error during agreement submission:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      alert('Failed to complete agreement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageWrapper}>
      <div style={formCard}>
        <h2 style={formTitle}>✍️ Sign the Agreement</h2>

        <div style={sigBlock}>
          <p>Renter's Signature</p>
          <SignaturePad ref={customerSigRef} canvasProps={{ style: canvasStyle }} />
        </div>

        <div style={sigBlock}>
          <p>Company Signature</p>
          <SignaturePad ref={ownerSigRef} canvasProps={{ style: canvasStyle }} />
        </div>

        <button onClick={handleSubmit} disabled={loading} style={submitButton}>
          {loading ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <span style={spinnerStyle}></span> Signing...
            </span>
          ) : (
            'Sign & Submit'
          )}
        </button>

        {pdfUrl && (
          <p style={{ marginTop: '20px' }}>
            ✅ Agreement saved. <a href={pdfUrl} target="_blank" rel="noopener noreferrer">View PDF</a>
          </p>
        )}
      </div>
    </div>
  );
}

// Styles
const pageWrapper = {
  backgroundColor: '#f4f6f8',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  padding: '40px 20px',
};

const formCard = {
  backgroundColor: '#fff',
  padding: '32px',
  borderRadius: '16px',
  maxWidth: '600px',
  width: '100%',
  boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
};

const formTitle = {
  fontSize: '24px',
  fontWeight: 700,
  marginBottom: '20px',
  color: '#111827',
  textAlign: 'center',
};

const sigBlock = {
  marginBottom: '20px',
};

const canvasStyle = {
  width: '100%',
  height: '120px',
  border: '1px solid #ccc',
  borderRadius: '8px',
  background: '#fff',
};

const submitButton = {
  width: '100%',
  padding: '14px',
  backgroundColor: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const spinnerStyle = {
  width: '16px',
  height: '16px',
  border: '3px solid #fff',
  borderTop: '3px solid rgba(255,255,255,0.3)',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

